import { isDateOverdue } from "@/lib/dateUtils";
import { severityRank } from "@/config/severity";
import type { InspectionDisplayStatus } from "./dashboardBento.helpers";

/**
 * Reine Helfer für die rollenspezifischen Dashboards (TES-1093):
 * „Mein Tag" (tester) und „Mängel-Inbox" (mangelbearbeiter).
 *
 * STRIKT real-schema-treu (D-CHK-015): alle Ableitungen basieren auf den realen
 * Enums `defect_status` (open/in_progress/resolved/closed/cancelled — KEIN
 * "verified") und `defect_severity` (low/medium/high/critical) sowie auf
 * `inspections.status`/`due_date`. Keine neuen Status, keine erfundenen Felder.
 */

// ---------------------------------------------------------------------------
// Mein Tag (tester)
// ---------------------------------------------------------------------------

export interface MyDayInspection {
  id: string;
  status: string;
  due_date: string | null;
  created_at: string;
  checklists: { name: string; version: string | null } | null;
  inspection_positions: { name: string } | null;
}

export interface MyDayView {
  /** Überfällige offene Prüfungen (due_date vor heute). */
  overdueCount: number;
  /** Heute fällige offene Prüfungen (due_date innerhalb des heutigen Tages). */
  dueTodayCount: number;
  /** Offene Prüfungen mit Status in_progress. */
  inProgressCount: number;
  /** Alle mir zugewiesenen offenen Prüfungen (pending + in_progress). */
  openTotal: number;
  /** Anstehende Prüfungen, überfällige zuerst, dann nach Fälligkeit (max. `limit`). */
  upcoming: MyDayInspection[];
}

/** due_date liegt innerhalb des heutigen Kalendertags (lokale Zeit, Referenz `now`). */
function isDueToday(dueDate: string | null, now: Date): boolean {
  if (!dueDate) return false;
  const due = new Date(dueDate);
  return (
    due.getFullYear() === now.getFullYear() &&
    due.getMonth() === now.getMonth() &&
    due.getDate() === now.getDate()
  );
}

/** Lexikografischer Vergleich numerischer Sortier-Schlüssel. */
function compareSortKeys(a: number[], b: number[]): number {
  const mismatch = a.findIndex((value, index) => value !== b[index]);
  return mismatch === -1 ? 0 : a[mismatch] - b[mismatch];
}

/** Sortier-Schlüssel: überfällig zuerst, dann Fälligkeit aufsteigend (ohne due_date ans Ende), dann Alter. */
function myDaySortKey(row: MyDayInspection): number[] {
  return [
    isDateOverdue(row.due_date) ? 0 : 1,
    row.due_date ? new Date(row.due_date).getTime() : Number.POSITIVE_INFINITY,
    new Date(row.created_at).getTime(),
  ];
}

/** Sortierung der anstehenden Prüfungen (siehe myDaySortKey). */
function sortMyDayInspections<T extends MyDayInspection>(rows: T[]): T[] {
  return [...rows].sort((a, b) => compareSortKeys(myDaySortKey(a), myDaySortKey(b)));
}

export function buildMyDayView(
  rows: MyDayInspection[],
  options: { limit?: number; now?: Date } = {},
): MyDayView {
  const { limit = 5, now = new Date() } = options;
  const sorted = sortMyDayInspections(rows);

  return {
    overdueCount: rows.filter((row) => isDateOverdue(row.due_date)).length,
    dueTodayCount: rows.filter((row) => isDueToday(row.due_date, now)).length,
    inProgressCount: rows.filter((row) => row.status === "in_progress").length,
    openTotal: rows.length,
    upcoming: sorted.slice(0, limit),
  };
}

/** Offene Mängel gesamt aus den Dashboard-Stats (notStarted + aktiv + zu verifizieren). */
export function countOpenDefects(
  stats:
    | { defectsNotStarted: number; defectsActive: number; defectsToVerify: number }
    | null
    | undefined,
): number {
  if (!stats) return 0;
  return stats.defectsNotStarted + stats.defectsActive + stats.defectsToVerify;
}

/** Anzeige-Status einer Prüfung (identische Ableitung wie Dashboard.getDisplayStatus). */
export function getInspectionDisplayStatus(inspection: {
  status: string;
  due_date: string | null;
}): InspectionDisplayStatus {
  if (inspection.status === "completed" || inspection.status === "cancelled") {
    return inspection.status;
  }
  if (isDateOverdue(inspection.due_date)) {
    return "overdue";
  }
  return inspection.status as InspectionDisplayStatus;
}

// ---------------------------------------------------------------------------
// Mängel-Inbox (mangelbearbeiter)
// ---------------------------------------------------------------------------

export interface InboxDefect {
  id: string;
  title: string;
  severity: string;
  status: string;
  due_date: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
}

/**
 * Inbox-Sortierung (Owner-Matrix: „nach Schwere/Alter"):
 * Schweregrad absteigend (critical → low), bei Gleichstand das älteste zuerst.
 */
export function sortInboxDefects<T extends Pick<InboxDefect, "severity" | "created_at">>(
  rows: T[],
): T[] {
  return [...rows].sort((a, b) => {
    const bySeverity = severityRank(b.severity) - severityRank(a.severity);
    if (bySeverity !== 0) return bySeverity;
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  });
}

export interface InboxKpis {
  /** Mir zugewiesene offene Mängel (open + in_progress). */
  assignedOpen: number;
  /** Davon überfällig (due_date vor heute). */
  overdue: number;
  /** Davon kritisch oder hoch. */
  highSeverity: number;
  /** Davon in Bearbeitung. */
  inProgress: number;
}

export function buildInboxKpis(
  openRows: Pick<InboxDefect, "severity" | "status" | "due_date">[],
): InboxKpis {
  return {
    assignedOpen: openRows.length,
    overdue: openRows.filter((row) => isDateOverdue(row.due_date)).length,
    highSeverity: openRows.filter((row) => row.severity === "critical" || row.severity === "high")
      .length,
    inProgress: openRows.filter((row) => row.status === "in_progress").length,
  };
}
